import { useState } from "react"

import { DatePicker, DatePickerProps } from "@material-ui/pickers"
import { css, cx } from "@emotion/css"

import { ReactComponent as CalendarIcon } from "assets/campaign/calendar.svg"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import IconButton from "@material-ui/core/IconButton"
import InputAdornment from "@material-ui/core/InputAdornment"
import { startOfDay } from "date-fns"
import { useAuthenticated } from "providers/A12n"
import { useField } from "formik"
import { useI18n } from "providers/I18n"
import { zonedTimeToUtc } from "date-fns-tz"

const styles = {
  btn: css`
    margin-right: -0.5rem;
  `,
  label: css`
    transform: translate(40px, 18px) scale(1) !important;
  `,
  smallLabel: css`
    transform: translate(40px, 10px) scale(1) !important;
  `,
  focused: css`
    border: 2px solid var(--primary-main) !important;
  `,
  focusedError: css`
    border: 2px solid var(--red);
  `,
  labelShrink: css`
    color: var(--primary-main);
  `,
  calendarIcon: css`
    > g {
      path {
        fill: var(--dark-grey);
      }
      > g > path {
        fill: var(--dark-grey);
      }
    }
  `,
  startAdornment: css`
    padding-left: var(--spacing-m);
  `,
  outlinedInput: css`
    padding-left: var(--spacing-xs) !important;
  `,
}

export type DateInputProps = {
  name: string
  onChange: (date: Date | null, name: string) => void
  value: string | Date | undefined
  dropdownIcon?: boolean
  hasStartAdornment?: boolean
} & Omit<DatePickerProps, "name" | "onChange">

export function DateInput(props: DateInputProps) {
  const {
    name,
    value,
    onChange,
    dropdownIcon,
    InputProps,
    hasStartAdornment,
    error,
    fullWidth = true,
    onClose,
    size = "medium",
    ...rest
  } = props
  const { meta } = useAuthenticated()
  const { timeZone = "Etc/UTC" } = meta ?? {}
  const [open, setOpen] = useState<boolean>(false)

  const handleChange = (d: Date | null) => {
    if (d) {
      /*
        date range inputs return the date at start of day
        this input returns the current date
        when we convert dates to ISOString
        these two may have one calendar day difference
        resulting in validation errors

        so we make the change here to return the date at start of day too
        as we don't care about precision in hours or down anywhere in the app.

        in addition we want to convert the date to the user settings timezone.
        the date picker displays the date according to the user settings 
        but the date sent to the backend needs to be converted to UTC iso string 
        considering the browser's auto-conversion to the locale timezone 
        which also has to be inverted to user settings timezone
      */
      const _d = zonedTimeToUtc(startOfDay(d), timeZone)
      onChange(_d, name)
    } else {
      onChange(null, name)
    }
  }

  const handleOpen = () => {
    setOpen(!open)
  }
  const handleClose = () => {
    setOpen(!open)
    onClose?.()
  }
  return (
    <DatePicker
      format="P"
      variant="inline"
      inputVariant="outlined"
      fullWidth={fullWidth}
      disableToolbar={true}
      allowKeyboardControl={true}
      error={error}
      size={size}
      {...rest}
      name={name}
      value={value || null}
      onChange={handleChange}
      onOpen={handleOpen}
      onClose={handleClose}
      InputLabelProps={{
        shrink: !!value || open,
        classes: {
          shrink: cx({ [styles.labelShrink]: open }),
          outlined: cx({
            [size === "small" ? styles.smallLabel : styles.label]:
              !open && !value,
          }),
        },
      }}
      InputProps={{
        classes: {
          notchedOutline: cx(
            { [styles.focused]: open && !error },
            { [styles.focusedError]: open && !!error },
          ),
          adornedStart: styles.startAdornment,
          input: styles.outlinedInput,
        },
        startAdornment: hasStartAdornment ? (
          <CalendarIcon className={styles.calendarIcon} />
        ) : undefined,
        endAdornment: dropdownIcon ? (
          <InputAdornment position="end">
            <IconButton size="small" classes={{ root: styles.btn }}>
              <DropdownIcon />
            </IconButton>
          </InputAdornment>
        ) : undefined,
        ...InputProps,
      }}
    />
  )
}

export type DatePickerFormikProps = Omit<
  DateInputProps,
  "value" | "onChange" | "error"
>

export function DatePickerFormik(props: DatePickerFormikProps) {
  const [{ value }, meta, { setValue }] = useField<string>(props.name)
  const { t } = useI18n()

  const hasError = !!(meta.touched && meta.error)
  const helper = hasError ? t(meta.error ?? "") : props.helperText

  const handleChange = (date: Date | null) => {
    if (!!date) {
      const _d = date.toISOString()
      setValue(_d)
    }
  }

  return (
    <DateInput
      {...props}
      value={value}
      onChange={handleChange}
      helperText={helper}
      error={hasError}
    />
  )
}
